import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchEtatOrdre } from '@/shared/api/pilotage';

/**
 * Suit un ordre S-Miles jusqu'à son état final, pour la popup de résultat.
 *
 * Retour de David du 28/09 : la popup restait sur « pas encore confirmée » alors que
 * l'installation avait exécuté. Le serveur relit désormais le résultat chez Hoymiles
 * juste après l'envoi (`hoymiles_pv_watch.suivre_ordre`) ; ce hook relit la TRACE en
 * base, ce qui ne coûte aucun appel Hoymiles, et s'arrête au premier état final.
 *
 * États : null (rien à suivre) · en_attente · reussi · echoue · sans_reponse.
 */
const RYTHME_MS = 3000;
// Au-delà, on cesse de relire : la veille continue côté serveur, et le journal dira la
// suite. Cinq minutes couvrent le suivi rapproché et un tour de veille entier.
const DUREE_MAX_MS = 5 * 60 * 1000;
const FINAUX = new Set(['reussi', 'echoue', 'sans_reponse']);

export default function useSuiviOrdre() {
  const [suivi, setSuivi] = useState(null);
  const minuterie = useRef(null);

  const arreter = useCallback(() => {
    clearInterval(minuterie.current);
    minuterie.current = null;
    setSuivi(null);
  }, []);

  const suivre = useCallback((operation) => {
    clearInterval(minuterie.current);
    if (!operation) { setSuivi(null); return; }
    setSuivi({ operation, resultat: 'en_attente' });
    const debut = Date.now();
    const relire = async () => {
      if (Date.now() - debut > DUREE_MAX_MS) { clearInterval(minuterie.current); return; }
      try {
        const etat = await fetchEtatOrdre(operation);
        setSuivi({ operation, resultat: etat.resultat, libelle: etat.libelle });
        if (FINAUX.has(etat.resultat)) clearInterval(minuterie.current);
      } catch { /* on retente au battement suivant */ }
    };
    minuterie.current = setInterval(relire, RYTHME_MS);
  }, []);

  useEffect(() => () => clearInterval(minuterie.current), []);

  return { suivi, suivre, arreter };
}
